import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { LandingPage } from "./pages/LandingPage";
import { EditorPage } from "./pages/EditorPage";
import { RequireAuth } from "./components/auth/RequireAuth";

// M-11 — lazy-load everything except the two most common entry
// points (landing + editor). Each of these becomes its own chunk,
// downloaded on demand; the initial bundle drops by a meaningful
// amount on slow connections.
const ProjectsPage = lazy(() =>
  import("./pages/ProjectsPage").then((m) => ({ default: m.ProjectsPage })),
);
const NotFoundPage = lazy(() =>
  import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })),
);
const SignupPage = lazy(() =>
  import("./pages/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const LoginPage = lazy(() =>
  import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const VerifyPage = lazy(() =>
  import("./pages/VerifyPage").then((m) => ({ default: m.VerifyPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("./pages/ForgotPasswordPage").then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import("./pages/ResetPasswordPage").then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const SettingsPage = lazy(() =>
  import("./pages/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const SharedProjectPage = lazy(() =>
  import("./pages/SharedProjectPage").then((m) => ({
    default: m.SharedProjectPage,
  })),
);
const UpgradePage = lazy(() =>
  import("./pages/UpgradePage").then((m) => ({ default: m.UpgradePage })),
);
const UpgradeSuccessPage = lazy(() =>
  import("./pages/UpgradeSuccessPage").then((m) => ({
    default: m.UpgradeSuccessPage,
  })),
);
const UpgradeCancelPage = lazy(() =>
  import("./pages/UpgradeCancelPage").then((m) => ({
    default: m.UpgradeCancelPage,
  })),
);

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-slate-500">
      Loading…
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/share/:token" element={<SharedProjectPage />} />

        {/* Auth-first pivot: /app now requires a session. The landing
            page funnels new visitors to /signup, returning visitors to
            /login. Guests are redirected to /login by RequireAuth. */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <EditorPage />
            </RequireAuth>
          }
        />
        <Route
          path="/app/project/:id"
          element={
            <RequireAuth>
              <EditorPage />
            </RequireAuth>
          }
        />
        <Route
          path="/projects"
          element={
            <RequireAuth>
              <ProjectsPage />
            </RequireAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireAuth>
              <SettingsPage />
            </RequireAuth>
          }
        />

        {/* M-02 Billing routes — /upgrade is public so unauthenticated
            users can see pricing. Success/cancel are also public because
            Stripe redirects there without going through RequireAuth. */}
        <Route path="/upgrade" element={<UpgradePage />} />
        <Route path="/upgrade/success" element={<UpgradeSuccessPage />} />
        <Route path="/upgrade/cancel" element={<UpgradeCancelPage />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
